import { SNUserPrefs, FillItemContent } from '@standardnotes/models';
import { AbstractService, SyncEvent, ApplicationStage, PreferencesServiceEvent, ApplicationEvent, StorageKey, } from '@standardnotes/services';
import { ContentType } from '@standardnotes/domain-core';
export class PreferencesService extends AbstractService {
    constructor(singletons, items, mutator, sync, storage, internalEventBus) {
        super(internalEventBus);
        this.singletons = singletons;
        this.mutator = mutator;
        this.sync = sync;
        this.storage = storage;
        this.internalEventBus = internalEventBus;
        this.shouldReload = true;
        this.reloading = false;
        this.localPreferences = {};
        this.removeItemObserver = items.addObserver(ContentType.TYPES.UserPrefs, () => {
            this.shouldReload = true;
        });
        this.removeSyncObserver = sync.addEventObserver((event) => {
            if (event === SyncEvent.SyncCompletedWithAllItemsUploaded || event === SyncEvent.LocalDataIncrementalLoad) {
                void this.reload();
            }
        });
    }
    deinit() {
        var _a, _b;
        (_a = this.removeItemObserver) === null || _a === void 0 ? void 0 : _a.call(this);
        (_b = this.removeSyncObserver) === null || _b === void 0 ? void 0 : _b.call(this);
        this.singletons = undefined;
        this.mutator = undefined;
        super.deinit();
    }
    async handleEvent(event) {
        var _a;
        if (event.type === ApplicationEvent.ApplicationStageChanged) {
            const stage = event.payload.stage;
            if (stage === ApplicationStage.LoadedDatabase_12) {
                /** Try to read preferences singleton from storage */
                this.preferences = this.singletons.findSingleton(ContentType.TYPES.UserPrefs, SNUserPrefs.singletonPredicate);
                if (this.preferences) {
                    void this.notifyEvent(PreferencesServiceEvent.PreferencesChanged);
                }
            }
            else if (stage === ApplicationStage.StorageDecrypted_09) {
                this.localPreferences = (_a = this.storage.getValue(StorageKey.LocalPreferences)) !== null && _a !== void 0 ? _a : {};
                void this.notifyEvent(PreferencesServiceEvent.LocalPreferencesChanged);
            }
        }
    }
    getLocalValue(key, defaultValue) {
        var _a;
        return (_a = this.localPreferences[key]) !== null && _a !== void 0 ? _a : defaultValue;
    }
    getValue(key, defaultValue) {
        var _a, _b;
        return (_b = (_a = this.preferences) === null || _a === void 0 ? void 0 : _a.getPref(key)) !== null && _b !== void 0 ? _b : defaultValue;
    }
    setLocalValue(key, value) {
        this.localPreferences[key] = value;
        this.storage.setValue(StorageKey.LocalPreferences, this.localPreferences);
        void this.notifyEvent(PreferencesServiceEvent.LocalPreferencesChanged);
    }
    async setValue(key, value) {
        await this.setValueDetached(key, value);
        void this.notifyEvent(PreferencesServiceEvent.PreferencesChanged);
        void this.sync.sync({ sourceDescription: 'PreferencesService.setValue' });
    }
    async setValueDetached(key, value) {
        if (!this.preferences) {
            return;
        }
        this.preferences = (await this.mutator.changeItem(this.preferences, (m) => {
            m.setPref(key, value);
        }));
    }
    async reload() {
        if (!this.shouldReload || this.reloading) {
            return;
        }
        this.reloading = true;
        try {
            const previousRef = this.preferences;
            this.preferences = await this.singletons.findOrCreateContentTypeSingleton(ContentType.TYPES.UserPrefs, FillItemContent({}));
            if ((previousRef === null || previousRef === void 0 ? void 0 : previousRef.uuid) !== this.preferences.uuid ||
                this.preferences.userModifiedDate > previousRef.userModifiedDate) {
                void this.notifyEvent(PreferencesServiceEvent.PreferencesChanged);
            }
            this.shouldReload = false;
        }
        finally {
            this.reloading = false;
        }
    }
}
